import type { LucideIcon } from "lucide-react";
import {
  BadgeCheck,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  CalendarRange,
  CircleUserRound,
  ClipboardList,
  LayoutDashboard,
  Landmark,
  LogOut,
  MapPinned,
  Menu,
  Settings,
  WalletCards,
  UserRoundCheck,
  UsersRound,
  X,
} from "lucide-react";

import type { AdminUser } from "./types";

export type View =
  | "dashboard"
  | "reports"
  | "leads"
  | "clients"
  | "bookings"
  | "schedule"
  | "catalog"
  | "availability"
  | "teams"
  | "employees"
  | "regions"
  | "quickbooks"
  | "square"
  | "notifications";

const workspace: Array<[LucideIcon, string, View]> = [
  [LayoutDashboard, "Dashboard", "dashboard"],
  [BarChart3, "Reports", "reports"],
  [CircleUserRound, "Leads", "leads"],
  [UsersRound, "Clients", "clients"],
];
const operations: Array<[LucideIcon, string, View]> = [
  [CalendarDays, "Booking requests", "bookings"],
  [CalendarDays, "Schedule", "schedule"],
  [ClipboardList, "Services & add-ons", "catalog"],
  [CalendarRange, "Availability rules", "availability"],
];
const organization: Array<[LucideIcon, string, View]> = [
  [BriefcaseBusiness, "Teams", "teams"],
  [UserRoundCheck, "Employees", "employees"],
  [MapPinned, "Regions", "regions"],
];
const integrations: Array<[LucideIcon, string, View]> = [
  [WalletCards, "Square", "square"],
  [Landmark, "QuickBooks", "quickbooks"],
];
const communications: Array<[LucideIcon, string, View]> = [
  [Bell, "Notifications", "notifications"],
];

export function Sidebar({
  current,
  open,
  onNavigate,
  onClose,
}: {
  current: View;
  open: boolean;
  onNavigate: (view: View) => void;
  onClose: () => void;
}) {
  return (
    <>
      {open ? (
        <button className="sidebar-scrim" aria-label="Close navigation" onClick={onClose} />
      ) : null}
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <div className="sidebar-top">
          <Brand compact />
          <button
            className="icon-button sidebar-close"
            aria-label="Close navigation"
            onClick={onClose}
          >
            <X />
          </button>
        </div>
        <nav aria-label="Operations navigation">
          <NavGroup label="Workspace" items={workspace} current={current} onNavigate={onNavigate} />
          <NavGroup
            label="Operations"
            items={operations}
            current={current}
            onNavigate={onNavigate}
            section
          />
          <NavGroup
            label="Integrations"
            items={integrations}
            current={current}
            onNavigate={onNavigate}
            section
          />
          <NavGroup
            label="Communications"
            items={communications}
            current={current}
            onNavigate={onNavigate}
            section
          />
          <NavGroup
            label="Organization"
            items={organization}
            current={current}
            onNavigate={onNavigate}
            section
          />
          <button disabled title="Coming in a later milestone">
            <Settings /> Settings
          </button>
        </nav>
        <div className="sidebar-note">
          <BadgeCheck />
          <div>
            <strong>TLC standard</strong>
            <span>Clear, careful, human.</span>
          </div>
        </div>
      </aside>
    </>
  );
}

function NavGroup({
  label,
  items,
  current,
  onNavigate,
  section = false,
}: {
  label: string;
  items: Array<[LucideIcon, string, View]>;
  current: View;
  onNavigate: (view: View) => void;
  section?: boolean;
}) {
  return (
    <>
      <span className={`nav-label ${section ? "nav-section" : ""}`}>{label}</span>
      {items.map(([Icon, text, view]) => (
        <button
          key={view}
          className={current === view ? "active" : ""}
          onClick={() => onNavigate(view)}
        >
          <Icon /> {text}
        </button>
      ))}
    </>
  );
}

export function Header({
  user,
  onMenu,
  onLogout,
}: {
  user: AdminUser;
  onMenu: () => void;
  onLogout: () => void;
}) {
  return (
    <header className="topbar">
      <button className="icon-button mobile-menu" aria-label="Open navigation" onClick={onMenu}>
        <Menu />
      </button>
      <div className="topbar-label">
        <span className="live-dot" /> Operations online
      </div>
      <div className="user-menu">
        <span className="avatar">{initials(user.name)}</span>
        <span className="user-copy">
          <strong>{user.name}</strong>
          <small>{user.roles[0]}</small>
        </span>
        <button className="icon-button" aria-label="Sign out" onClick={onLogout}>
          <LogOut />
        </button>
      </div>
    </header>
  );
}

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? "compact" : ""}`}>
      <img src="/tlc-round-logo.webp" alt="" />
      <div>
        <strong>
          TLC <i>|</i> Cleaning <i>|</i> Co.
        </strong>
        <span>Tender · Love · Care</span>
      </div>
    </div>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}
